import type { HighlightWithMetadata } from "../types/highlightNavigation";
import { HighlightListItem } from "./HighlightListItem";
import { ui } from "@/strings";

interface HighlightListProps {
  highlights: HighlightWithMetadata[];
  selectedHighlightId: string | null;
  isTeacher: boolean;
  showCompleted?: boolean;
  onSelectHighlight: (id: string) => void;
  onToggleComplete?: (id: string) => void;
  onEditHighlight?: (highlight: HighlightWithMetadata) => void;
  onDeleteHighlight?: (highlight: HighlightWithMetadata) => void;
  /** Function to check if a resolved highlight can be unresolved */
  canUnresolve?: (highlightId: string) => boolean;
}

export function HighlightList({
  highlights,
  selectedHighlightId,
  isTeacher,
  showCompleted = false,
  onSelectHighlight,
  onToggleComplete,
  onEditHighlight,
  onDeleteHighlight,
  canUnresolve,
}: HighlightListProps) {
  // Separate active and completed highlights
  const activeHighlights = highlights.filter((h) => !h.isCompleted);
  const completedHighlights = highlights.filter((h) => h.isCompleted);

  if (highlights.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <p className="text-text-theme-muted text-sm">{ui.highlightNavigation.emptyState}</p>
        <p className="text-text-theme-muted text-xs mt-1">{ui.highlightNavigation.emptyStateHint}</p>
      </div>
    );
  }

  // Teachers see a single flat list in document order, with
  // resolved state indicated via the badge/icon on each item.
  if (isTeacher) {
    return (
      <div className="flex flex-col gap-2">
        {highlights.map((highlight) => (
          <HighlightListItem
            key={highlight.id}
            highlight={highlight}
            isSelected={selectedHighlightId === highlight.id}
            isTeacher={true}
            onClick={() => onSelectHighlight(highlight.id)}
            onEdit={onEditHighlight ? () => onEditHighlight(highlight) : undefined}
            onDelete={onDeleteHighlight ? () => onDeleteHighlight(highlight) : undefined}
            canUnresolve={canUnresolve ? canUnresolve(highlight.id) : false}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Active highlights section */}
      {activeHighlights.length > 0 && (
        <div className="flex flex-col gap-2">
          {!isTeacher && completedHighlights.length > 0 && (
            <h3 className="text-xs font-semibold text-text-theme-secondary uppercase tracking-wide px-3">
              {ui.highlightNavigation.activeSection}
            </h3>
          )}
          {activeHighlights.map((highlight) => (
            <HighlightListItem
              key={highlight.id}
              highlight={highlight}
              isSelected={selectedHighlightId === highlight.id}
              isTeacher={isTeacher}
              onClick={() => onSelectHighlight(highlight.id)}
              onToggleComplete={
                !isTeacher && onToggleComplete
                  ? () => onToggleComplete(highlight.id)
                  : undefined
              }
              onEdit={
                isTeacher && onEditHighlight && highlight.type === "ai"
                  ? () => onEditHighlight(highlight)
                  : undefined
              }
              onDelete={
                isTeacher && onDeleteHighlight ? () => onDeleteHighlight(highlight) : undefined
              }
              canUnresolve={canUnresolve ? canUnresolve(highlight.id) : false}
            />
          ))}
        </div>
      )}

      {/* Completed highlights section */}
      {showCompleted && completedHighlights.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold text-text-theme-secondary uppercase tracking-wide px-3">
            {ui.highlightNavigation.completedSection}
          </h3>
          {completedHighlights.map((highlight) => (
            <HighlightListItem
              key={highlight.id}
              highlight={highlight}
              isSelected={selectedHighlightId === highlight.id}
              isTeacher={isTeacher}
              onClick={() => onSelectHighlight(highlight.id)}
              onToggleComplete={
                !isTeacher && onToggleComplete
                  ? () => onToggleComplete(highlight.id)
                  : undefined
              }
              onEdit={
                isTeacher && onEditHighlight && highlight.type === "ai"
                  ? () => onEditHighlight(highlight)
                  : undefined
              }
              onDelete={
                isTeacher && onDeleteHighlight ? () => onDeleteHighlight(highlight) : undefined
              }
              canUnresolve={canUnresolve ? canUnresolve(highlight.id) : false}
            />
          ))}
        </div>
      )}
    </div>
  );
}
